import { useState, FormEvent } from "react";

export default function EstimateSection() {
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [charCount, setCharCount] = useState(0);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const detail = (form.elements.namedItem("detail") as HTMLTextAreaElement).value;
    if (detail.length > 500) {
      alert("상세 내용은 500자 이내로 입력해주세요.");
      return;
    }
    setSubmitting(true);
    const formData = new FormData(form);
    const params = new URLSearchParams();
    formData.forEach((value, key) => {
      params.append(key, value.toString());
    });
    try {
      await fetch("https://readdy.ai/api/form/d6sen1g37h4e5cfhpmb0", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: params.toString(),
      });
      setSubmitted(true);
    } catch {
      setSubmitted(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="estimate" className="py-14 lg:py-24 bg-zinc-900">
      <div className="max-w-7xl mx-auto px-4 lg:px-6">
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-14 items-start">
          {/* Left Info */}
          <div className="text-white">
            <span className="inline-block text-blue-400 font-bold text-xs lg:text-sm tracking-widest uppercase mb-2 lg:mb-3">Contact</span>
            <h2 className="text-2xl lg:text-4xl font-black leading-tight mb-4 lg:mb-5">
              무료 견적을<br />
              지금 바로 받아보세요
            </h2>
            <p className="text-zinc-400 text-sm lg:text-base leading-relaxed mb-6 lg:mb-8">
              간단한 정보만 입력하시면 빠르게 연락드립니다.<br />
              24시간 이내 전문가가 직접 확인 후 안내해드립니다.
            </p>

            {/* Contact Info */}
            <div className="flex flex-col gap-4 lg:gap-5 mb-6 lg:mb-8">
              <a href="tel:010-2684-4892" className="flex items-center gap-3 lg:gap-4 cursor-pointer group">
                <div className="w-10 h-10 lg:w-12 lg:h-12 flex items-center justify-center bg-blue-500 rounded-xl">
                  <i className="ri-phone-fill text-white text-lg lg:text-xl" />
                </div>
                <div>
                  <div className="text-zinc-400 text-xs">24시간 즉시 상담</div>
                  <div className="text-white text-lg lg:text-xl font-bold group-hover:text-blue-400 transition-colors">010-2684-4892</div>
                </div>
              </a>
              <div className="flex items-center gap-3 lg:gap-4">
                <div className="w-10 h-10 lg:w-12 lg:h-12 flex items-center justify-center bg-zinc-700 rounded-xl">
                  <i className="ri-time-fill text-blue-400 text-lg lg:text-xl" />
                </div>
                <div>
                  <div className="text-zinc-400 text-xs">운영 시간</div>
                  <div className="text-white text-sm lg:text-base font-semibold">365일 24시간 연중무휴</div>
                </div>
              </div>
              <div className="flex items-center gap-3 lg:gap-4">
                <div className="w-10 h-10 lg:w-12 lg:h-12 flex items-center justify-center bg-zinc-700 rounded-xl">
                  <i className="ri-map-pin-2-fill text-blue-400 text-lg lg:text-xl" />
                </div>
                <div>
                  <div className="text-zinc-400 text-xs">출동 지역</div>
                  <div className="text-white text-sm lg:text-base font-semibold">서울 · 경기 · 인천 수도권 전지역</div>
                </div>
              </div>
            </div>

            {/* Quick CTA */}
            <a
              href="tel:010-2684-4892"
              className="inline-flex items-center gap-2 lg:gap-3 bg-blue-500 hover:bg-blue-400 text-white font-bold text-base lg:text-lg px-6 lg:px-8 py-3.5 lg:py-4 rounded-full transition-all cursor-pointer whitespace-nowrap"
            >
              <span className="w-5 lg:w-6 h-5 lg:h-6 flex items-center justify-center">
                <i className="ri-phone-fill text-lg lg:text-xl" />
              </span>
              지금 바로 전화하기
            </a>
          </div>

          {/* Right Form */}
          <div className="bg-white rounded-2xl p-5 lg:p-8">
            {submitted ? (
              <div className="flex flex-col items-center justify-center py-14 text-center">
                <div className="w-16 h-16 flex items-center justify-center bg-blue-100 rounded-full mb-4">
                  <i className="ri-check-line text-blue-500 text-3xl" />
                </div>
                <h3 className="text-zinc-900 text-xl font-black mb-2">견적 요청 완료!</h3>
                <p className="text-zinc-500 text-sm">빠른 시간 내에 전문가가 연락드리겠습니다.<br />급하신 분은 아래 번호로 바로 전화 주세요.</p>
                <a href="tel:010-2684-4892" className="mt-6 inline-flex items-center gap-2 bg-blue-500 text-white font-bold px-6 py-3 rounded-full cursor-pointer whitespace-nowrap">
                  <i className="ri-phone-fill" />
                  010-2684-4892
                </a>
              </div>
            ) : (
              <>
                <h3 className="text-zinc-900 font-black text-lg lg:text-xl mb-5 lg:mb-6">무료 견적 요청서</h3>
                <form
                  data-readdy-form
                  onSubmit={handleSubmit}
                  className="flex flex-col gap-4"
                >
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-zinc-700 text-sm font-semibold mb-1.5">이름 *</label>
                      <input
                        name="name"
                        type="text"
                        required
                        placeholder="홍길동"
                        className="w-full border border-zinc-200 rounded-lg px-4 py-2.5 text-sm text-zinc-800 focus:outline-none focus:border-blue-400"
                      />
                    </div>
                    <div>
                      <label className="block text-zinc-700 text-sm font-semibold mb-1.5">연락처 *</label>
                      <input
                        name="phone"
                        type="tel"
                        required
                        placeholder="010-0000-0000"
                        className="w-full border border-zinc-200 rounded-lg px-4 py-2.5 text-sm text-zinc-800 focus:outline-none focus:border-blue-400"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-zinc-700 text-sm font-semibold mb-1.5">이메일</label>
                    <input
                      name="email"
                      type="email"
                      placeholder="example@email.com"
                      className="w-full border border-zinc-200 rounded-lg px-4 py-2.5 text-sm text-zinc-800 focus:outline-none focus:border-blue-400"
                    />
                  </div>
                  <div>
                    <label className="block text-zinc-700 text-sm font-semibold mb-1.5">주소 (시/구 정도면 OK)</label>
                    <input
                      name="address"
                      type="text"
                      placeholder="예) 서울시 강남구"
                      className="w-full border border-zinc-200 rounded-lg px-4 py-2.5 text-sm text-zinc-800 focus:outline-none focus:border-blue-400"
                    />
                  </div>
                  <div>
                    <label className="block text-zinc-700 text-sm font-semibold mb-1.5">건물 유형</label>
                    <select
                      name="buildingType"
                      className="w-full border border-zinc-200 rounded-lg px-4 py-2.5 text-sm text-zinc-800 focus:outline-none focus:border-blue-400 bg-white cursor-pointer"
                    >
                      <option value="">선택해주세요</option>
                      <option value="아파트">아파트</option>
                      <option value="빌라">빌라/다세대</option>
                      <option value="단독주택">단독주택</option>
                      <option value="상가">상가/사무실</option>
                      <option value="기타">기타</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-zinc-700 text-sm font-semibold mb-1.5">
                      상세 내용 <span className="text-zinc-400 font-normal text-xs">({charCount}/500자)</span>
                    </label>
                    <textarea
                      name="detail"
                      rows={4}
                      placeholder="누수 위치, 증상, 발생 시기 등을 알려주시면 더욱 정확한 상담이 가능합니다."
                      maxLength={500}
                      onChange={(e) => setCharCount(e.target.value.length)}
                      className="w-full border border-zinc-200 rounded-lg px-4 py-2.5 text-sm text-zinc-800 focus:outline-none focus:border-blue-400 resize-none"
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full bg-blue-500 hover:bg-blue-400 disabled:opacity-60 text-white font-bold py-3.5 rounded-full transition-all cursor-pointer whitespace-nowrap mt-2"
                  >
                    {submitting ? "제출 중..." : "무료 견적 요청하기"}
                  </button>
                  <p className="text-zinc-400 text-xs text-center">급하신 분은 <a href="tel:010-2684-4892" className="text-blue-500 font-bold cursor-pointer">010-2684-4892</a> 로 바로 전화주세요</p>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
